import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, ChevronDown, Plus, Globe, Lock, Trash2, Play, RefreshCw } from "lucide-react";
import { SavedQuery } from "@/types/savedQuery";
import { useSavedQueries, useDeleteSavedQuery, useTrackQueryExecution } from "@/hooks/savedQueries";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";

interface SavedQueriesDropdownProps {
	onApply: (query: SavedQuery) => void;
	onUpdate?: (query: SavedQuery) => void;
	onSaveClick: () => void;
	currentUserId?: string;
	activeQueryId?: string | null;
}

export const SavedQueriesDropdown = ({
	onApply,
	onUpdate,
	onSaveClick,
	currentUserId,
	activeQueryId
}: SavedQueriesDropdownProps) => {
	const { t } = useTranslation();
	const { data: queries, isLoading } = useSavedQueries();
	const deleteQuery = useDeleteSavedQuery();
	const trackExecution = useTrackQueryExecution();
	const [queryToDelete, setQueryToDelete] = useState<SavedQuery | null>(null);

	const handleApply = (query: SavedQuery) => {
		onApply(query);
		trackExecution.mutate(query.id);
	};

	const handleDelete = async () => {
		if (queryToDelete) {
			await deleteQuery.mutateAsync(queryToDelete.id);
			setQueryToDelete(null);
		}
	};

	// Helper to check if query is in simple filter format (not advanced query builder format)
	const isSimpleFilterFormat = (query: SavedQuery): boolean => {
		const def = query.query_definition;
		// Advanced query builder format has rootGroup and targetEntity
		// Simple filter format has searchTerm, skillsFilter, statusFilter, etc.
		return !('rootGroup' in def) && !('targetEntity' in def);
	};

	// Filter queries to only show simple filter format queries (not advanced query builder queries)
	const simpleFormatQueries = queries?.filter(isSimpleFilterFormat) || [];

	// Find the active query
	const activeQuery = activeQueryId ? simpleFormatQueries.find(q => q.id === activeQueryId) : null;
	const isOwnerOfActiveQuery = activeQuery?.created_by === currentUserId;

	// Group queries by ownership
	const myQueries = simpleFormatQueries.filter(q => q.created_by === currentUserId) || [];
	const publicQueries = simpleFormatQueries.filter(q => q.is_public && q.created_by !== currentUserId) || [];
	const sharedQueries = simpleFormatQueries.filter(q =>
		!q.is_public &&
		q.created_by !== currentUserId &&
		q.shared_with_user_ids?.includes(currentUserId || '')
	) || [];

	const hasQueries = (myQueries.length + publicQueries.length + sharedQueries.length) > 0;

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="outline" className="gap-2">
						<Search className="h-4 w-4" />
						{t("savedQueries.title", "Saved Queries")}
						<ChevronDown className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent className="w-72" align="start">
					{/* Update Active Query */}
					{activeQuery && isOwnerOfActiveQuery && onUpdate && (
						<>
							<DropdownMenuItem 
								onClick={() => onUpdate(activeQuery)} 
								className="gap-2 cursor-pointer text-blue-600 dark:text-blue-400"
							>
								<RefreshCw className="h-4 w-4" />
								{t("savedQueries.updateQuery", "Update")} "{activeQuery.name}"
							</DropdownMenuItem>
							<DropdownMenuSeparator />
						</>
					)}

					{/* Save Current Filters */}
					<DropdownMenuItem onClick={onSaveClick} className="gap-2 cursor-pointer">
						<Plus className="h-4 w-4" />
						{t("savedQueries.saveCurrentFilters", "Save current filters")}
					</DropdownMenuItem>

					<DropdownMenuSeparator />

					{isLoading ? (
						<div className="p-2 space-y-2">
							<Skeleton className="h-8 w-full" />
							<Skeleton className="h-8 w-full" />
							<Skeleton className="h-8 w-full" />
						</div>
					) : !hasQueries ? (
						<div className="p-4 text-center text-sm text-gray-500">
							<Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
							<p>{t("savedQueries.empty", "No saved queries")}</p>
							<p className="text-xs mt-1">
								{t("savedQueries.emptyDescription", "Save your current filters to quickly apply them later")}
							</p>
						</div>
					) : (
						<>
							{/* My Queries */}
							{myQueries.length > 0 && (
								<DropdownMenuGroup>
									<DropdownMenuLabel className="text-xs text-gray-500">
										{t("savedQueries.groups.myQueries", "My Queries")}
									</DropdownMenuLabel>
									{myQueries.map((query) => (
										<QueryMenuItem
											key={query.id}
											query={query}
											onApply={handleApply}
											onDelete={() => setQueryToDelete(query)}
											isOwner={true}
											t={t}
										/>
									))}
								</DropdownMenuGroup>
							)}

							{/* Public Queries */}
							{publicQueries.length > 0 && (
								<>
									<DropdownMenuSeparator />
									<DropdownMenuGroup>
										<DropdownMenuLabel className="text-xs text-gray-500">
											{t("savedQueries.groups.publicQueries", "Public Queries")}
										</DropdownMenuLabel>
										{publicQueries.map((query) => (
											<QueryMenuItem
												key={query.id}
												query={query}
												onApply={handleApply}
												isOwner={false}
												t={t}
											/>
										))}
									</DropdownMenuGroup>
								</>
							)}

							{/* Shared Queries */}
							{sharedQueries.length > 0 && (
								<>
									<DropdownMenuSeparator />
									<DropdownMenuGroup>
										<DropdownMenuLabel className="text-xs text-gray-500">
											{t("savedQueries.groups.sharedWithMe", "Shared with Me")}
										</DropdownMenuLabel>
										{sharedQueries.map((query) => (
											<QueryMenuItem
												key={query.id}
												query={query}
												onApply={handleApply}
												isOwner={false}
												t={t}
											/>
										))}
									</DropdownMenuGroup>
								</>
							)}
						</>
					)}
				</DropdownMenuContent>
			</DropdownMenu>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={!!queryToDelete} onOpenChange={() => setQueryToDelete(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("savedQueries.delete.title", "Delete Query")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("savedQueries.delete.description", "Are you sure you want to delete this saved query? This action cannot be undone.")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("common.cancel", "Cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDelete}
							className="bg-red-600 hover:bg-red-700"
						>
							{t("common.delete", "Delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};

interface QueryMenuItemProps {
	query: SavedQuery;
	onApply: (query: SavedQuery) => void;
	onDelete?: () => void;
	isOwner: boolean;
	t: (key: string, fallback?: string) => string;
}

const QueryMenuItem = ({ query, onApply, onDelete, isOwner, t }: QueryMenuItemProps) => {
	return (
		<DropdownMenuItem
			className="flex items-start gap-2 p-2 cursor-pointer"
			onSelect={(e) => {
				e.preventDefault();
				onApply(query);
			}}
		>
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2">
					{query.is_public ? (
						<Globe className="h-3 w-3 text-gray-400 flex-shrink-0" />
					) : (
						<Lock className="h-3 w-3 text-gray-400 flex-shrink-0" />
					)}
					<span className="font-medium text-sm truncate">{query.name}</span>
				</div>
				{query.description && (
					<p className="text-xs text-gray-500 truncate mt-0.5 ml-5">
						{query.description}
					</p>
				)}
				<div className="text-xs text-gray-400 mt-1 ml-5 flex items-center gap-2">
					<Play className="h-3 w-3" />
					{query.execution_count} {t("savedQueries.usage.times", "times")}
					{query.last_executed_at && (
						<span>
							· {formatDistanceToNow(new Date(query.last_executed_at), { addSuffix: true })}
						</span>
					)}
				</div>
			</div>
			{isOwner && onDelete && (
				<Button
					variant="ghost"
					size="sm"
					className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
					onClick={(e) => {
						e.stopPropagation();
						onDelete();
					}}
				>
					<Trash2 className="h-3 w-3" />
				</Button>
			)}
		</DropdownMenuItem>
	);
};
